/*eslint complexity: ["error", 27]*/
import React from 'react';
import { Form } from 'react-bootstrap';

import { Grid } from '../../../common/components/Grid/Grid';
import { RegisterNumberingButton } from '../RegisterNumbering/RegisterNumberingButton';
import {
  GridCountFunction,
  GridGetterFunction,
} from '../../../common/components/Grid/GridDataTypes';
import { GetHealthcareFacilityDto } from '../../../common/spdCore/autogenerated/spdApiClient';
import { useAuth } from '../../../common/hooks/useAuth';
import { Role } from '../../../common/spdCore/authorization/roles';
import momencik from '../../../common/momencik';

interface HealthcareFacilitiesSearchProps {
  getData: GridGetterFunction;
  getCount: GridCountFunction;
  onRowClick: (key: string) => Promise<void> | void | undefined;
  refresh?: boolean;
  isHistorical?: boolean;
}

export const healthcareFacilityForGrid = (
  healthcareFacility: GetHealthcareFacilityDto,
  onSetAsMain?: (facilityId: string, facilityName: string) => void,
  onSetAsOnline?: (facilityId: string, facilityName: string) => void,
) => {
  return {
    key: healthcareFacility.id,
    values: {
      'healthcareFacilityData.nameDoctor':
        healthcareFacility.healthcareFacilityData?.[0]?.nameDoctor,
      'healthcareFacilityData.namePsychological':
        healthcareFacility.healthcareFacilityData?.[0]?.namePsychological,
      'healthcareFacility.displayName': healthcareFacility.displayName,
      'healthcareFacilityData.city':
        healthcareFacility.healthcareFacilityData?.[0]?.city,
      'healthcareFacilityData.address':
        healthcareFacility.healthcareFacilityData?.[0]?.address,
      'healthcareFacilityData.validFrom': momencik(
        healthcareFacility.healthcareFacilityData?.[0]?.validFrom,
      ),
      'healthcareFacilityData.entryContentPsychological':
        healthcareFacility.healthcareFacilityData?.[0]
          ?.entryContentPsychological,
      'healthcareFacilityData.entryContentDoctor':
        healthcareFacility.healthcareFacilityData?.[0]?.entryContentDoctor,
      'healthcareFacility.active': healthcareFacility.active ? 'TAK' : '',
      'healthcareFacility.online': (
        <Form.Check
          type="checkbox"
          checked={healthcareFacility.online || false}
          onChange={() => {
            if (!healthcareFacility.online) {
              onSetAsOnline?.(
                healthcareFacility.id,
                healthcareFacility.displayName || '',
              );
            }
          }}
          onClick={(e: React.MouseEvent) => e.stopPropagation()}
        />
      ),
      'healthcareFacility.main': (
        <Form.Check
          type="checkbox"
          checked={healthcareFacility.main || false}
          onChange={() => {
            if (!healthcareFacility.main) {
              onSetAsMain?.(
                healthcareFacility.id,
                healthcareFacility.displayName || '',
              );
            }
          }}
          onClick={(e: React.MouseEvent) => e.stopPropagation()}
        />
      ),
      'duty.name': healthcareFacility.duty?.name,
      'healthcareFacilityData.time':
        (healthcareFacility.healthcareFacilityData?.[0]?.openMondayTime?.slice(
          0,
          5,
        ) || '') +
        '  -  ' +
        (healthcareFacility.healthcareFacilityData?.[0]?.closeMondayTime?.slice(
          0,
          5,
        ) || ''),
      'actions.registerNumbering': (
        <RegisterNumberingButton facilityId={healthcareFacility.id} />
      ),
    },
    isInactive: !healthcareFacility.active,
  };
};

export const HealthcareFacilitySearch: React.FC<
  HealthcareFacilitiesSearchProps
> = props => {
  const auth = useAuth();
  const isSuperAdmin = auth.check([Role.SuperAdmin]);

  const columns = [
    {
      header: 'Nazwa wyświetlana',
      property: 'healthcareFacility.displayName',
    },
    {
      header: 'Adres',
      property: 'healthcareFacilityData.address',
      noSorting: true,
    },
    {
      header: 'Nazwa placówki (lekarska)',
      property: 'healthcareFacilityData.nameDoctor',
      noSorting: true,
    },
    {
      header: 'Nazwa placówki (psychologiczna)',
      property: 'healthcareFacilityData.namePsychological',
      noSorting: true,
    },
    {
      header: 'Wpis (lekarska)',
      property: 'healthcareFacilityData.entryContentDoctor',
      noSorting: true,
    },
    {
      header: 'Wpis (psychologiczna)',
      property: 'healthcareFacilityData.entryContentPsychological',
      noSorting: true,
    },
    {
      header: 'Ważne od',
      property: 'healthcareFacilityData.validFrom',
      noSorting: true,
    },
    { header: 'Aktywna', property: 'healthcareFacility.active' },
    ...(props.isHistorical
      ? []
      : [
          {
            header: 'Online',
            property: 'healthcareFacility.online',
            noSorting: true,
          },
          {
            header: 'Główna',
            property: 'healthcareFacility.main',
            noSorting: true,
          },
        ]),
    { header: 'Dyżur', property: 'duty.name', noSorting: true },
    {
      header: 'Godziny pracy w poniedziałek',
      property: 'healthcareFacilityData.time',
      noSorting: true,
    },
    ...(isSuperAdmin
      ? [
          {
            header: 'Akcje',
            property: 'actions.registerNumbering',
            noSorting: true,
          },
        ]
      : []),
  ];

  return (
    <Grid
      getData={props.getData}
      getDataCount={props.getCount}
      columns={columns}
      showFilter={true}
      initialOrderColumn="healthcareFacility.displayName"
      availablePageSizes={[10, 20, 50]}
      onRowClick={props.onRowClick}
      emptyGridMessage="Brak placówek w systemie"
      enableMultiSelect={false}
      refresh={props.refresh}
      showExport={true}
    />
  );
};
